import { $createCodeNode } from "@lexical/code";
import { TOGGLE_LINK_COMMAND } from "@lexical/link";
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { $createHeadingNode, $createQuoteNode } from "@lexical/rich-text";
import {
  $createParagraphNode,
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  UNDO_COMMAND,
} from "lexical";

// editor.focus() places the selection and leaves DOM focus to the browser's
// selection handling; the root is focused when that did not happen, as
// Lexical's AutoFocusPlugin does.
function focusEditor(editor) {
  editor.focus(() => {
    const root = editor.getRootElement();
    if (root && !root.contains(document.activeElement)) {
      root.focus({ preventScroll: true });
    }
  });
}

// Behavior for each toolbar control kind, matched to its descriptor in
// controls.js by `kind`. An omitted field falls back to the plugin's default
// (inactive, enabled, no-op), so a control that only renders a menu needs no run.
export const BEHAVIOR_BY_KIND = {
  format: {
    isActive: ctx => Boolean(ctx.state.formats[ctx.control.value]),
    run: ctx =>
      ctx.editor.dispatchCommand(FORMAT_TEXT_COMMAND, ctx.control.value),
  },
  heading: {
    isActive: ctx => ctx.state.blockType === ctx.control.value,
    run: ctx =>
      ctx.setBlock(
        ctx.active
          ? $createParagraphNode
          : () => $createHeadingNode(ctx.control.value)
      ),
  },
  quote: {
    isActive: ctx => ctx.state.blockType === "quote",
    run: ctx =>
      ctx.setBlock(ctx.active ? $createParagraphNode : $createQuoteNode),
  },
  codeBlock: {
    isActive: ctx => ctx.state.blockType === "code",
    run: ctx =>
      ctx.setBlock(ctx.active ? $createParagraphNode : $createCodeNode),
  },
  list: {
    isActive: ctx => ctx.state.blockType === ctx.control.value,
    run: ctx => {
      if (ctx.active) {
        ctx.editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      } else {
        ctx.editor.dispatchCommand(
          ctx.control.value === "number"
            ? INSERT_ORDERED_LIST_COMMAND
            : INSERT_UNORDERED_LIST_COMMAND,
          undefined
        );
      }
    },
  },
  align: {
    isActive: ctx => ctx.state.alignment === ctx.control.value,
    run: ctx =>
      ctx.editor.dispatchCommand(
        FORMAT_ELEMENT_COMMAND,
        ctx.active ? "" : ctx.control.value
      ),
  },
  mention: {
    // Inserting the trigger character is what opens the typeahead; the plugin
    // takes over from the caret position.
    run: ctx => {
      ctx.editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          selection.insertNodes([$createTextNode("@")]);
        }
      });
      ctx.editor.focus();
    },
  },
  addLink: {
    run: ctx => ctx.openLinkDialog(),
  },
  removeLink: {
    isDisabled: ctx => !ctx.state.isLink,
    run: ctx => ctx.editor.dispatchCommand(TOGGLE_LINK_COMMAND, null),
  },
  undo: {
    isDisabled: ctx => !ctx.canUndo,
    run: ctx => ctx.editor.dispatchCommand(UNDO_COMMAND, undefined),
  },
  redo: {
    isDisabled: ctx => !ctx.canRedo,
    run: ctx => ctx.editor.dispatchCommand(REDO_COMMAND, undefined),
  },
  attach: {
    isDisabled: ctx => !ctx.onAttach,
    run: ctx => ctx.onAttach?.(),
  },
  template: {
    isDisabled: ctx => !ctx.onSelectTemplate,
  },
  category: {
    isDisabled: ctx => !ctx.onChangeCategory,
  },
  manageCategories: {
    isDisabled: ctx => !ctx.onManageCategories,
    run: ctx => ctx.onManageCategories?.(),
  },
  fullscreen: {
    isActive: ctx => Boolean(ctx.isFullscreen),
    isDisabled: ctx => !ctx.onToggleFullscreen,
    run: ctx => {
      ctx.onToggleFullscreen?.();
      focusEditor(ctx.editor);
    },
  },
};
